import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import type {
  InvestmentRefreshResponse,
  InvestmentsAutomationStatus,
  InvestmentsCachePullOperationStatus,
  InvestmentsCachePullResponse,
  InvestmentsRuntimeReloadResponse,
  InvestmentsPublicationComparison,
} from "@features/investments/api";
import { InvestmentsWorkspaceShell } from "@features/investments/components/workspace-shell";
import { InvestmentsLoadingScreen, InvestmentsPageState } from "@features/investments/components/page-state";
import {
  useInvestmentsCachePullStatusQuery,
  useInvestmentsRuntimeStatusQuery,
  useInvestmentsPublicationStatusQuery,
	useInvestmentsDashboardQuery,
  usePullInvestmentTickerFromBucketMutation,
  usePullInvestmentsCacheMutation,
  useRefreshInvestmentMutation,
  useReloadInvestmentsRuntimeMutation,
} from "@features/investments/queries";
import "@views/investments/index.scss";

type OperatorActionResult = {
  tone: "success" | "error";
  meta: string;
  title: string;
  lines: string[];
};

const productionWorkflowCommands = [
  {
    label: "Refresh and upload cache",
    command: "scripts/refresh-investments-and-upload.sh",
  },
  {
    label: "Publish runtime config",
    command: "go run ./cmd/cli --config-dir config investments runtime-config publish --target production",
  },
];

const OperatorScreen = (): JSX.Element => {
  const queryClient = useQueryClient();
  const runtimeQuery = useInvestmentsRuntimeStatusQuery();
  const publicationQuery = useInvestmentsPublicationStatusQuery();
	const dashboardQuery = useInvestmentsDashboardQuery();
  const reloadMutation = useReloadInvestmentsRuntimeMutation();
  const fullPullMutation = usePullInvestmentsCacheMutation();
  const tickerPullMutation = usePullInvestmentTickerFromBucketMutation();
  const refreshMutation = useRefreshInvestmentMutation();
  const [ticker, setTicker] = React.useState("");
  const [latestResult, setLatestResult] = React.useState<OperatorActionResult | null>(null);
  const [activeOperation, setActiveOperation] = React.useState<InvestmentsCachePullOperationStatus | null>(null);
  const cachePullStatusQuery = useInvestmentsCachePullStatusQuery(activeOperation != null);
  const elapsedSeconds = useElapsedSeconds(activeOperation?.startedAt ? Date.parse(activeOperation.startedAt) : null);

  React.useEffect(() => {
    const status = cachePullStatusQuery.data;
    if (activeOperation == null || status == null || status.operationId !== activeOperation.operationId) {
      return;
    }

    if (status.status === "running") {
      setActiveOperation(status);
      return;
    }

    if (status.status === "succeeded" && status.result) {
      setActiveOperation(null);
      setLatestResult(buildCachePullResult(status.result));
      void queryClient.invalidateQueries({ queryKey: ["investments"] });
      return;
    }

    if (status.status === "failed") {
      setActiveOperation(null);
      setLatestResult(buildCachePullFailureResult(status));
      void queryClient.invalidateQueries({ queryKey: ["investments"] });
    }
  }, [activeOperation, cachePullStatusQuery.data, queryClient]);

  if (runtimeQuery.isLoading && !runtimeQuery.data) {
    return <InvestmentsLoadingScreen message="Loading console..." />;
  }

  if (runtimeQuery.error && !runtimeQuery.data) {
    return <InvestmentsPageState message={`Failed to load console: ${runtimeQuery.error.message}`} />;
  }

  const runtime = runtimeQuery.data ?? null;
  const automationIsRunning = publicationQuery.data?.automation?.state === "running";
  const localDataIsNewer = publicationQuery.data?.data.state === "running-newer";
  const hasActiveOperation = activeOperation != null || automationIsRunning;
	const analysisAudit = (() => {
	  const rows = dashboardQuery.data?.equityRows || [];
	  const count = (predicate: (row: typeof rows[number]) => boolean) => rows.filter(predicate).length;
	  const modelCounts = rows.reduce<Record<string, number>>((counts, row) => {
		const model = row.analysisModel || "v1 / unclassified";
		counts[model] = (counts[model] || 0) + 1;
		return counts;
	  }, {});
	  const dispositionCounts = rows.reduce<Record<string, number>>((counts, row) => {
		const disposition = row.screeningDisposition || row.finalAction || "unavailable";
		counts[disposition] = (counts[disposition] || 0) + 1;
		return counts;
	  }, {});
	  const confidenceCounts = rows.reduce<Record<string, number>>((counts, row) => {
		const confidence = row.dataConfidence || "unavailable";
		counts[confidence] = (counts[confidence] || 0) + 1;
		return counts;
	  }, {});
	  return {
		total: rows.length,
		v2: count((row) => Boolean(row.screeningDisposition)),
		research: count((row) => row.screeningDisposition === "RESEARCH_REQUIRED"),
		lowConfidence: count((row) => row.dataConfidence === "LOW"),
		unsupported: count((row) => row.analysisModelSupported === false),
		stale: count((row) => row.researchStale === true),
		modelCounts,
		dispositionCounts,
		confidenceCounts,
	  };
	})();

  function resolveTickerOrShowError(actionLabel: string): string | null {
    const normalizedTicker = ticker.trim().toUpperCase();
    if (normalizedTicker !== "") {
      return normalizedTicker;
    }
    setLatestResult({
      tone: "error",
      meta: "Validation",
      title: `${actionLabel} failed`,
      lines: ["Enter a ticker symbol before running a ticker action."],
    });
    return null;
  }

  async function handleReloadRuntime(): Promise<void> {
    try {
      const result = await reloadMutation.mutateAsync();
      setLatestResult(buildReloadResult(result));
    } catch (err) {
      setLatestResult({
        tone: "error",
        meta: "Runtime config",
        title: "Runtime reload failed",
        lines: [formatError(err)],
      });
    }
  }

  async function handlePullFullCache(): Promise<void> {
    setLatestResult(null);
    try {
      const operation = await fullPullMutation.mutateAsync();
      setActiveOperation(operation);
    } catch (err) {
      setActiveOperation(null);
      setLatestResult({
        tone: "error",
        meta: "Full cache pull",
        title: "Bucket full pull failed",
        lines: [formatError(err)],
      });
    }
  }

  async function handlePullTickerFromBucket(): Promise<void> {
    const normalizedTicker = resolveTickerOrShowError("Bucket ticker pull");
    if (normalizedTicker == null) {
      return;
    }
    try {
      const result = await tickerPullMutation.mutateAsync(normalizedTicker);
      setTicker(normalizedTicker);
      setLatestResult(buildCachePullResult(result));
    } catch (err) {
      setLatestResult({
        tone: "error",
        meta: "Ticker cache pull",
        title: `Bucket ticker pull failed for ${normalizedTicker}`,
        lines: [formatError(err)],
      });
    }
  }

  async function handleRefreshTicker(): Promise<void> {
    const normalizedTicker = resolveTickerOrShowError("Provider refresh");
    if (normalizedTicker == null) {
      return;
    }

    try {
      const result = await refreshMutation.mutateAsync(normalizedTicker);
      setTicker(normalizedTicker);
      setLatestResult(buildRefreshResult(result));
    } catch (err) {
      setLatestResult({
        tone: "error",
        meta: "Provider refresh",
        title: `Provider refresh failed for ${normalizedTicker}`,
        lines: [formatError(err)],
      });
    }
  }

  return (
    <InvestmentsWorkspaceShell
      title="Console"
      description="Manage runtime config and cache data for the running service behind this origin."
      headerMeta={<div className="dashboard-generated-at">Same-origin admin controls</div>}
    >
      <div className="operator-console-page">
        <section className="investments-card workflow-card operator-console-status-panel">
          <div className="operator-console-panel-header">
            <div className="operator-console-panel-copy">
              <div className="section-eyebrow">Runtime + Cache</div>
              <h2>Running service status</h2>
              <p>
                The Console acts on the service behind this browser origin. Bucket pull hydrates local cache from published bucket data.
                Provider refresh recomputes from upstream and uploads the result back to the bucket.
              </p>
            </div>
          </div>

          {runtimeQuery.error && runtime && (
            <div className="ticker-refresh-status error">
              Runtime status may be stale: {runtimeQuery.error.message}
            </div>
          )}

          <div className="operator-runtime-grid operator-runtime-grid--dense">
            <article className="operator-runtime-metric">
              <div className="operator-runtime-label">Environment</div>
              <div className="operator-runtime-value">{runtime?.environment || "N/A"}</div>
            </article>
            <article className="operator-runtime-metric">
              <div className="operator-runtime-label">Bucket Target</div>
              <div className="operator-runtime-value">{runtime?.bucketTarget || "N/A"}</div>
            </article>
            <article className="operator-runtime-metric">
              <div className="operator-runtime-label">Bucket Prefix</div>
              <div className="operator-runtime-value operator-runtime-value--code" title={runtime?.bucketPrefix || "N/A"}>
                {runtime?.bucketPrefix || "N/A"}
              </div>
            </article>
            <article className="operator-runtime-metric">
              <div className="operator-runtime-label">Cache Root</div>
              <div className="operator-runtime-value operator-runtime-value--code" title={runtime?.cacheRoot || "N/A"}>
                {runtime?.cacheRoot || "N/A"}
              </div>
            </article>
            <article className="operator-runtime-metric">
              <div className="operator-runtime-label">Runtime Source</div>
              <div className="operator-runtime-value">{runtime?.source || "N/A"}</div>
            </article>
            <article className="operator-runtime-metric operator-runtime-metric--wide">
              <div className="operator-runtime-label">Version</div>
              <div className="operator-runtime-value operator-runtime-value--code" title={runtime?.version || "N/A"}>
                {runtime?.version || "N/A"}
              </div>
            </article>
            <article className="operator-runtime-metric">
              <div className="operator-runtime-label">Generated</div>
              <div className="operator-runtime-value">{formatDateTime(runtime?.generatedAt)}</div>
            </article>
            <article className="operator-runtime-metric">
              <div className="operator-runtime-label">Tickers</div>
              <div className="operator-runtime-value">{runtime?.tickerCount != null ? String(runtime.tickerCount) : "N/A"}</div>
            </article>
          </div>

          <div className="operator-status-note">
            {runtimeQuery.isFetching ? "Refreshing runtime status..." : "Runtime status updates after runtime reloads, bucket pulls, and provider refreshes."}
          </div>
        </section>

        <section className="investments-card workflow-card operator-publication-panel">
          <div className="operator-console-panel-header"><div className="operator-console-panel-copy"><div className="section-eyebrow">Publication drift</div><h2>Running vs latest published</h2><p>Versions are content hashes. Status is checked from the active service cache and bucket publication manifests.</p></div></div>
          {publicationQuery.error && <div className="ticker-refresh-status error">Publication metadata is unavailable: {publicationQuery.error.message}</div>}
          {publicationQuery.data && (
            <div className="operator-publication-grid">
              <PublicationComparisonPanel kind="config" title="Runtime config" comparison={publicationQuery.data.config} />
              <PublicationComparisonPanel kind="data" title="Cache data" comparison={publicationQuery.data.data} />
            </div>
          )}
          {publicationQuery.data?.warnings?.length ? <div className="operator-publication-warnings">{publicationQuery.data.warnings.map(warning=><div key={warning}>{warning}</div>)}</div> : null}
          <div className="operator-status-note">{publicationQuery.isFetching?"Checking bucket publication metadata...":`Checked ${formatDateTime(publicationQuery.data?.checkedAt)}`}</div>
        </section>

		<section className="investments-card workflow-card operator-analysis-audit-panel">
		  <div className="operator-console-panel-header"><div className="operator-console-panel-copy"><div className="section-eyebrow">Analysis V2</div><h2>Evidence audit</h2><p>Counts come from the active cache and runtime research policy.</p></div></div>
		  {dashboardQuery.error ? <div className="ticker-refresh-status error">Analysis audit unavailable: {dashboardQuery.error.message}</div> : (
			<div className="operator-runtime-grid">
			  <article className="operator-runtime-metric"><div className="operator-runtime-label">V2 analyzed</div><div className="operator-runtime-value">{analysisAudit.v2}/{analysisAudit.total}</div></article>
			  <article className="operator-runtime-metric"><div className="operator-runtime-label">Research required</div><div className="operator-runtime-value"><Link to="/investments/coverage?disposition=RESEARCH_REQUIRED">{analysisAudit.research}</Link></div></article>
			  <article className="operator-runtime-metric"><div className="operator-runtime-label">Low confidence</div><div className="operator-runtime-value"><Link to="/investments/coverage?confidence=LOW">{analysisAudit.lowConfidence}</Link></div></article>
			  <article className="operator-runtime-metric"><div className="operator-runtime-label">Unsupported models</div><div className="operator-runtime-value"><Link to="/investments/coverage?evidence=unsupported">{analysisAudit.unsupported}</Link></div></article>
			  <article className="operator-runtime-metric"><div className="operator-runtime-label">Stale research</div><div className="operator-runtime-value"><Link to="/investments/coverage?evidence=stale">{analysisAudit.stale}</Link></div></article>
			  <article className="operator-runtime-metric operator-runtime-metric--wide"><div className="operator-runtime-label">Models</div><div className="operator-runtime-value">{Object.entries(analysisAudit.modelCounts).sort(([a], [b]) => a.localeCompare(b)).map(([model, count]) => `${model}: ${count}`).join(" · ") || "N/A"}</div></article>
			  <article className="operator-runtime-metric operator-runtime-metric--wide"><div className="operator-runtime-label">Dispositions</div><div className="operator-runtime-value">{Object.entries(analysisAudit.dispositionCounts).sort(([a], [b]) => a.localeCompare(b)).map(([disposition, count]) => `${disposition.replace(/_/g, " ")}: ${count}`).join(" · ") || "N/A"}</div></article>
			  <article className="operator-runtime-metric operator-runtime-metric--wide"><div className="operator-runtime-label">Confidence</div><div className="operator-runtime-value">{Object.entries(analysisAudit.confidenceCounts).sort(([a], [b]) => a.localeCompare(b)).map(([confidence, count]) => `${confidence}: ${count}`).join(" · ") || "N/A"}</div></article>
			</div>
		  )}
		</section>

        {publicationQuery.data?.automation && (
          <ScheduledRefreshPanel automation={publicationQuery.data.automation} />
        )}

        <div className="operator-console-main-grid">
          <section className="investments-card workflow-card operator-console-actions-panel">
            <div className="operator-console-panel-header">
              <div className="operator-console-panel-copy">
                <div className="section-eyebrow">Data + Runtime</div>
                <h2>Operator actions</h2>
                <p>Use the CLI to publish into the bucket. Use this Console to sync the running service with that published state.</p>
              </div>
            </div>

            <div className="operator-console-sequence" aria-label="Manual publication order">
              <div className="operator-console-sequence-title">Manual publication order</div>
              <ol>
                <li>Publish cache data</li>
                <li>Publish runtime config</li>
                <li>Reload runtime config</li>
                <li>Pull cache into service</li>
              </ol>
            </div>

            <div className="operator-console-action-list">
              <div className="operator-console-action-row">
                <div className="operator-console-action-copy">
                  <h3>Reload runtime config</h3>
                  <p>Loads the newest published runtime-config bundle for this origin without touching cache data.</p>
                </div>
                <button
                  type="button"
                  className="operator-console-button"
                  onClick={() => void handleReloadRuntime()}
                  disabled={reloadMutation.isPending || hasActiveOperation}
                >
                  {reloadMutation.isPending ? "Reloading..." : "Reload Runtime Config"}
                </button>
              </div>

              <div className="operator-console-action-row operator-console-action-row--warn">
                <div className="operator-console-action-copy">
                  <h3>Recover full cache from bucket</h3>
                  <p>Recovery action: replaces the running service cache with the current environment bucket and prunes local extras.</p>
                  <p className="operator-console-warning-copy">Use this only when you intentionally want the bucket publication to replace local data.</p>
                  {localDataIsNewer && (
                    <p className="operator-console-warning-copy" role="alert">
                      The local cache is newer than the bucket publication. Pulling now will replace that newer local data.
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  className="operator-console-button operator-console-button--warn"
                  onClick={() => void handlePullFullCache()}
                  disabled={fullPullMutation.isPending || hasActiveOperation}
                >
                  {fullPullMutation.isPending || activeOperation != null ? "Pulling..." : "Pull Full Cache Into Service"}
                </button>
              </div>

              <div className="operator-console-action-row operator-console-action-row--ticker">
                <div className="operator-console-action-copy">
                  <h3>Ticker data actions</h3>
                  <p>Bucket pull hydrates one published ticker cache file. Provider refresh recomputes from upstream and uploads the changed local cache back to the bucket.</p>
                </div>
                <label className="operator-console-inline-field" htmlFor="operator-ticker-input">
                  <span>Ticker</span>
                  <input
                    id="operator-ticker-input"
                    className="watchlist-input operator-console-ticker-input"
                    type="text"
                    inputMode="text"
                    autoCapitalize="characters"
                    autoCorrect="off"
                    spellCheck={false}
                    placeholder="MRVL"
                    value={ticker}
                    onChange={(event) => setTicker(event.target.value.toUpperCase())}
                  />
                </label>
                <div className="operator-console-action-buttons">
                  <button
                    type="button"
                    className="operator-console-button"
                    onClick={() => void handlePullTickerFromBucket()}
                    disabled={tickerPullMutation.isPending || hasActiveOperation}
                  >
                    {tickerPullMutation.isPending ? "Pulling..." : "Pull One Ticker"}
                  </button>
                  <button
                    type="button"
                    className="operator-console-button"
                    onClick={() => void handleRefreshTicker()}
                    disabled={refreshMutation.isPending || hasActiveOperation}
                  >
                    {refreshMutation.isPending ? "Refreshing..." : "Refresh + Upload Ticker"}
                  </button>
                </div>
              </div>
            </div>
          </section>

          <section className="investments-card workflow-card operator-console-log-panel">
            <div className="operator-console-panel-header">
              <div className="operator-console-panel-copy">
                <div className="section-eyebrow">Latest Result</div>
                <h2>Operator activity</h2>
                <p>Every action reports the target, data movement, and completion time for this running service.</p>
              </div>
            </div>

            {activeOperation != null ? (
              <div className="operator-active-operation" role="status" aria-live="polite">
                <div className="operator-active-operation-header">
                  <div>
                    <div className="operator-active-kicker">Active operation</div>
                    <strong>Pull full cache from bucket</strong>
                  </div>
                  <span className="operator-active-badge">{activeOperation.status === "running" ? "Running" : activeOperation.status}</span>
                </div>
                <div className="operator-active-progress" aria-hidden="true">
                  <span />
                </div>
                <dl className="operator-active-grid">
                  <div>
                    <dt>Elapsed</dt>
                    <dd>{formatElapsedSeconds(elapsedSeconds * 1000)}</dd>
                  </div>
                  <div>
                    <dt>Target</dt>
                    <dd>{activeOperation.target}</dd>
                  </div>
                  <div>
                    <dt>Bucket Prefix</dt>
                    <dd>{activeOperation.prefix}</dd>
                  </div>
                  <div>
                    <dt>Downloaded</dt>
                    <dd>{formatObjectProgress(activeOperation.downloaded, activeOperation.remoteObjects)}</dd>
                  </div>
                  <div>
                    <dt>Deleted</dt>
                    <dd>{activeOperation.deleted}</dd>
                  </div>
                  <div>
                    <dt>Bytes</dt>
                    <dd>{activeOperation.bytesTransferred}</dd>
                  </div>
                </dl>
                {activeOperation.lastKey && <div className="operator-active-last-key">Last object: {activeOperation.lastKey}</div>}
                <p>This mirrors the bucket cache into the running service and prunes local extras.</p>
              </div>
            ) : latestResult == null ? (
              <div className="workflow-empty">Run a runtime reload, bucket pull, or provider refresh to see the latest result here.</div>
            ) : (
              <div className={`ticker-refresh-status${latestResult.tone === "error" ? " error" : ""}`}>
                <div className="operator-result-meta">{latestResult.meta}</div>
                <strong>{latestResult.title}</strong>
                <ul className="operator-result-list">
                  {latestResult.lines.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        </div>

        <section className="investments-card workflow-card operator-console-runbook">
          <div className="operator-console-panel-header">
            <div className="operator-console-panel-copy">
              <div className="section-eyebrow">Production Publish</div>
              <h2>Common CLI commands</h2>
              <p>Run these from the repo root before activating the updated state in this Console.</p>
            </div>
          </div>
          <div className="operator-command-grid">
            {productionWorkflowCommands.map((entry) => (
              <div className="operator-command-card" key={entry.command}>
                <div>
                  <span>{entry.label}</span>
                  <code>{entry.command}</code>
                </div>
                <button
                  type="button"
                  className="operator-console-button"
                  onClick={() => void copyCommand(entry.command)}
                >
                  Copy
                </button>
              </div>
            ))}
          </div>
        </section>

        <details className="investments-card workflow-card operator-console-runbook">
          <summary>
            <span>New ticker workflow</span>
            <span className="operator-console-runbook-hint">CLI publish, then Console activate</span>
          </summary>
          <ol className="operator-checklist">
            <li>Edit local `tickers.yaml` and `families.yaml`.</li>
            <li>Run `go run ./cmd/cli --config-dir config investments sync --ticker MRVL --force`.</li>
            <li>Run `go run ./cmd/cli --config-dir config investments bucket upload --target production --ticker MRVL --prune=false`.</li>
            <li>Run `go run ./cmd/cli --config-dir config investments runtime-config publish --target production`.</li>
            <li>Open `/#/investments/operator` on the target origin and click `Reload Runtime Config`.</li>
            <li>Use `Pull One Ticker` to hydrate the running service from the published bucket cache. Use `Refresh + Upload Ticker` only when you want a fresh provider recompute and bucket upload from this service.</li>
          </ol>
        </details>
      </div>
    </InvestmentsWorkspaceShell>
  );
};

type PublicationPanelKind = "config" | "data";

function PublicationComparisonPanel({
  kind,
  title,
  comparison,
}: {
  kind: PublicationPanelKind;
  title: string;
  comparison: InvestmentsPublicationComparison;
}): JSX.Element {
  return (
    <article className="operator-publication-card">
      <div className="operator-publication-card-header">
        <h3>{title}</h3>
        <span className={`operator-publication-state state-${comparison.state}`}>
          {formatPublicationState(comparison.state)}
        </span>
      </div>
      <p>{comparison.message}</p>
      <div className="operator-publication-columns">
        <PublicationMetadataColumn kind={kind} label="Running / local" metadata={comparison.current} />
        <PublicationMetadataColumn kind={kind} label="Latest published" metadata={comparison.published} />
      </div>
      <div className="operator-publication-next-action">
        <strong>Next action</strong>
        <span>{publicationNextAction(kind, comparison.state)}</span>
      </div>
    </article>
  );
}

function PublicationMetadataColumn({
  kind,
  label,
  metadata,
}: {
  kind: PublicationPanelKind;
  label: string;
  metadata: InvestmentsPublicationComparison["current"];
}): JSX.Element {
  if (!metadata) {
    return (
      <div className="operator-publication-metadata">
        <strong>{label}</strong>
        <span>Unavailable</span>
      </div>
    );
  }

  return (
    <div className="operator-publication-metadata">
      <strong>{label}</strong>
      <span className="operator-publication-hash" title={metadata.version}>Version {shortHash(metadata.version)}</span>
      <span>Generated {formatDateTime(metadata.generatedAt)}</span>
      <span>{metadata.count} {kind === "config" ? "tickers" : "objects"}</span>
      {metadata.source && <span>Source {metadata.source}</span>}
      {metadata.target && <span>Target {metadata.target}</span>}
      {metadata.prefix && <span>Prefix {metadata.prefix}</span>}
      {metadata.sourceCommit && (
        <span title={metadata.sourceCommit}>Source commit {shortHash(metadata.sourceCommit)}</span>
      )}
      {metadata.runtimeConfigVersion && (
        <span title={metadata.runtimeConfigVersion}>Runtime config {shortHash(metadata.runtimeConfigVersion)}</span>
      )}
      {metadata.runtimeConfigSourceCommit && (
        <span title={metadata.runtimeConfigSourceCommit}>
          Runtime config commit {shortHash(metadata.runtimeConfigSourceCommit)}
        </span>
      )}
      {metadata.complete !== undefined && (
        <span className={`operator-publication-completeness ${metadata.complete ? "is-complete" : "is-incomplete"}`}>
          {metadata.complete ? "Complete mirror" : "Incomplete mirror"}
        </span>
      )}
    </div>
  );
}

function publicationNextAction(kind: PublicationPanelKind, state: string): string {
  const actions: Record<PublicationPanelKind, Record<string, string>> = {
    config: {
      current: "No action needed; the service is using the latest published runtime config.",
      "published-newer": "Reload Runtime Config to activate the latest published bundle.",
      "running-newer": "Publish the active runtime config if it should become the shared version.",
      different: "Compare the source commits, publish the intended bundle, then reload it.",
      "local-modified": "Reload the published runtime config, or publish the intentional local changes.",
      "incomplete-publication": "Republish the complete runtime-config bundle, then reload it.",
      unavailable: "Check runtime-config manifest access and validation before choosing a version.",
    },
    data: {
      current: "No action needed; the live cache matches the latest complete publication.",
      "published-newer": "Pull Full Cache Into Service to activate the latest published data.",
      "running-newer": "Run a full cache publication if the local cache should become the shared version.",
      different: "Compare the runtime-config identity, then pull or publish the intended cache.",
      "local-modified": "Pull the full cache to restore the publication, or publish the intentional local changes.",
      "incomplete-publication": "Run a full cache upload with pruning enabled to publish a complete mirror.",
      unavailable: "Check cache-manifest access and validation; operator actions remain available.",
    },
  };

  return actions[kind][state] ?? `Review the ${kind === "config" ? "runtime config" : "cache data"} versions before taking action.`;
}

function ScheduledRefreshPanel({ automation }: { automation: InvestmentsAutomationStatus }): JSX.Element {
  const stateLabel = formatPublicationState(automation.state);
  const hasFailures = automation.failedCount > 0 || automation.failedItems.length > 0 || automation.errors.length > 0;

  return (
    <section className="investments-card workflow-card operator-automation-panel" aria-labelledby="scheduled-refresh-heading">
      <div className="operator-console-panel-header">
        <div className="operator-console-panel-copy">
          <div className="section-eyebrow">Automation</div>
          <h2 id="scheduled-refresh-heading">Scheduled refresh</h2>
          <p>{automation.message}</p>
        </div>
        <span
          className={`operator-automation-state state-${automationStateTone(automation.state)}`}
          aria-label={`Scheduled refresh status: ${stateLabel}`}
        >
          {stateLabel}
        </span>
      </div>

      <div className="operator-automation-schedule">
        <span className="operator-runtime-label">Schedule</span>
        <strong>{automation.schedule.weekdays} at {automation.schedule.localTime}</strong>
        <span>{automation.schedule.timeZone}</span>
      </div>

      <dl className="operator-automation-time-grid">
        {(automation.schedulerStartedAt || automation.heartbeatAt || automation.currentScheduledFor) && (
          <>
            <AutomationTimeMetric label="Scheduler started" value={automation.schedulerStartedAt} />
            <AutomationTimeMetric label="Scheduler heartbeat" value={automation.heartbeatAt} />
            <AutomationTimeMetric label="Current schedule" value={automation.currentScheduledFor} />
          </>
        )}
        <AutomationTimeMetric label="Started" value={automation.startedAt} />
        <AutomationTimeMetric label="Completed" value={automation.completedAt} />
        <AutomationTimeMetric label="Last successful" value={automation.lastSuccessfulAt} />
        <AutomationTimeMetric label="Next expected" value={automation.nextExpectedAt} />
        <div>
          <dt>Duration</dt>
          <dd>{formatDurationSeconds(automation.durationSeconds)}</dd>
        </div>
      </dl>

      <div className="operator-automation-version-grid">
        <AutomationImageMetadata label="Service image" image={automation.serviceImage} />
        <AutomationImageMetadata label="Refresh image" image={automation.workerImage} />
        <AutomationVersionMetadata label="Runtime config" version={automation.activeRuntimeConfigVersion} />
        <AutomationVersionMetadata label="Active data" version={automation.activeDataVersion ?? automation.publishedDataVersion} />
      </div>

      <div className="operator-automation-count-grid" aria-label="Scheduled refresh result counts">
        <AutomationCount label="Normal" value={automation.normalCount} />
        <AutomationCount label="Deep refreshed" value={automation.deepRefreshedCount} />
        <AutomationCount label="Preserved stale" value={automation.preservedStaleCount} tone={automation.preservedStaleCount > 0 ? "warning" : undefined} />
        <AutomationCount label="Failed" value={automation.failedCount} tone={automation.failedCount > 0 ? "danger" : undefined} />
      </div>

      <p className="operator-status-note">
        Successful scheduled runs promote the staged cache directly into the service mount; no Pull Full Cache action is needed.
      </p>

      {hasFailures && (
        <div className="operator-automation-failures" role="alert">
          <h3>Refresh issues</h3>
          {automation.failedItems.length > 0 && (
            <div>
              <strong>Failed items</strong>
              <div className="operator-automation-failed-items">
                {automation.failedItems.map((item) => <span key={item}>{item}</span>)}
              </div>
            </div>
          )}
          {automation.errors.length > 0 && (
            <ul>
              {automation.errors.map((error, index) => <li key={`${index}-${error}`}>{error}</li>)}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}

function AutomationTimeMetric({ label, value }: { label: string; value?: string }): JSX.Element {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{value ? <time dateTime={value}>{formatDateTime(value)}</time> : "N/A"}</dd>
    </div>
  );
}

function AutomationImageMetadata({ label, image }: { label: string; image?: InvestmentsAutomationStatus["serviceImage"] }): JSX.Element {
  return (
    <article>
      <span className="operator-runtime-label">{label}</span>
      {image ? (
        <>
          <strong title={image.version}>{image.version || "N/A"}</strong>
          <span title={image.revision}>Revision {shortHash(image.revision)}</span>
          {image.role && <span>Role {image.role}</span>}
        </>
      ) : <strong>Unavailable</strong>}
    </article>
  );
}

function AutomationVersionMetadata({ label, version }: { label: string; version?: string }): JSX.Element {
  return (
    <article>
      <span className="operator-runtime-label">{label}</span>
      <strong title={version || "N/A"}>{shortHash(version || "")}</strong>
    </article>
  );
}

function AutomationCount({ label, value, tone }: { label: string; value: number; tone?: "warning" | "danger" }): JSX.Element {
  return (
    <article className={tone ? `tone-${tone}` : undefined}>
      <strong>{value}</strong>
      <span>{label}</span>
    </article>
  );
}

function shortHash(value:string):string{return value&&value.length>12?value.slice(0,12):value||"N/A"}
function formatPublicationState(value:string):string{return value.split("-").map(part=>part.charAt(0).toUpperCase()+part.slice(1)).join(" ")}

function automationStateTone(state: string): string {
  switch (state) {
    case "succeeded":
      return "succeeded";
    case "running":
      return "running";
    case "partial":
    case "overdue":
    case "unavailable":
      return "warning";
    case "failed":
      return "failed";
    default:
      return "neutral";
  }
}

function buildReloadResult(result: InvestmentsRuntimeReloadResponse): OperatorActionResult {
  return {
    tone: "success",
    meta: "Runtime config",
    title: result.reloaded ? "Runtime reload" : "Runtime already current",
    lines: [
      result.message || (result.reloaded ? "Applied the newest valid runtime bundle for this service." : "No newer valid runtime bundle was available."),
      `Source: ${result.runtime.source}`,
      `Version: ${result.runtime.version}`,
      `Generated: ${formatDateTime(result.runtime.generatedAt)}`,
      `Tickers: ${result.runtime.tickerCount}`,
    ],
  };
}

function buildCachePullResult(result: InvestmentsCachePullResponse): OperatorActionResult {
  const title = result.mode === "ticker"
    ? `Bucket ticker pull${result.ticker ? ` for ${result.ticker}` : ""}`
    : "Bucket full pull";
  return {
    tone: "success",
    meta: result.mode === "ticker" ? "Ticker cache pull" : "Full cache pull",
    title,
    lines: [
      `Target: ${result.target}`,
      `Bucket prefix: ${result.prefix}`,
      `Downloaded objects: ${result.downloaded}`,
      `Deleted local files: ${result.deleted}`,
      `Bytes transferred: ${result.bytesTransferred}`,
      `Completed: ${formatDateTime(result.completedAt)}`,
    ],
  };
}

function buildCachePullFailureResult(status: InvestmentsCachePullOperationStatus): OperatorActionResult {
  return {
    tone: "error",
    meta: `Full cache pull - ${formatElapsedSeconds(Date.now() - Date.parse(status.startedAt))}`,
    title: "Bucket full pull failed",
    lines: [
      `Target: ${status.target}`,
      `Bucket prefix: ${status.prefix}`,
      `Downloaded objects: ${formatObjectProgress(status.downloaded, status.remoteObjects)}`,
      `Deleted local files: ${status.deleted}`,
      `Bytes transferred: ${status.bytesTransferred}`,
      "This mirrors the bucket cache into the running service and prunes local extras.",
      status.error || "Cache pull failed before the service returned a detailed error.",
    ],
  };
}

function buildRefreshResult(result: InvestmentRefreshResponse): OperatorActionResult {
  return {
    tone: "success",
    meta: "Provider refresh",
    title: `Provider refresh for ${result.ticker}`,
    lines: [
      `Target: ${result.target}`,
      `Bucket prefix: ${result.prefix}`,
      `Uploaded objects: ${result.bucketSync.uploaded}`,
      `Deleted objects: ${result.bucketSync.deleted}`,
      `Bytes transferred: ${result.bucketSync.bytesTransferred}`,
      `Completed: ${formatDateTime(result.completedAt)}`,
    ],
  };
}

function formatDateTime(value?: string | null): string {
  if (!value) {
    return "N/A";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString([], {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatElapsedSeconds(milliseconds: number): string {
  const totalSeconds = Math.max(0, Math.floor(milliseconds / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes <= 0) {
    return `${seconds}s`;
  }
  return `${minutes}m ${seconds.toString().padStart(2, "0")}s`;
}

function formatDurationSeconds(value?: number): string {
  if (value == null || !Number.isFinite(value) || value < 0) {
    return "N/A";
  }
  const totalSeconds = Math.floor(value);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) {
    return `${hours}h ${minutes.toString().padStart(2, "0")}m`;
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds.toString().padStart(2, "0")}s`;
  }
  return `${seconds}s`;
}

function formatObjectProgress(current: number, total: number): string {
  if (total > 0) {
    return `${current}/${total}`;
  }
  return String(current);
}

function formatError(err: unknown): string {
  if (err instanceof Error && err.message) {
    return err.message;
  }
  return "Unexpected operator error.";
}

function useElapsedSeconds(startedAt: number | null): number {
  const [elapsedSeconds, setElapsedSeconds] = React.useState(0);

  React.useEffect(() => {
    if (startedAt == null) {
      setElapsedSeconds(0);
      return undefined;
    }

    const updateElapsed = () => {
      setElapsedSeconds(Math.max(0, Math.floor((Date.now() - startedAt) / 1000)));
    };

    updateElapsed();
    const interval = window.setInterval(updateElapsed, 1000);
    return () => window.clearInterval(interval);
  }, [startedAt]);

  return elapsedSeconds;
}

async function copyCommand(command: string): Promise<void> {
  if (!navigator.clipboard) {
    return;
  }
  try {
    await navigator.clipboard.writeText(command);
  } catch {
    // Clipboard access can be blocked outside secure browser contexts.
  }
}

export default OperatorScreen;
